import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  X,
  ArrowRight,
  ChevronDown,
  Building2,
  UserCircle,
  ShoppingBag,
  Zap,
  Star,
  Mic,
  Monitor,
  MessageSquare,
  Gift,
  BarChart3,
  Users,
  CreditCard,
  Shield,
  Calendar,
  Database,
  Smartphone,
  MapPin,
  TrendingUp,
  Brain,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  
  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small restaurants getting started',
      price: annual ? 39 : 49,
      period: '/month',
      highlight: false,
      features: [
        { name: 'Cloud POS System', icon: Monitor, included: true, description: 'Web-based POS accessible anywhere' },
        { name: 'Basic Scheduling', icon: Calendar, included: true, description: 'Staff scheduling & shifts' },
        { name: 'Customer Database', icon: Database, included: true, description: 'Store customer info & history' },
        { name: 'Basic Reporting', icon: BarChart3, included: true, description: 'Sales & performance reports' },
        { name: 'Email Support', icon: MessageSquare, included: true, description: 'Email support (24-48hr response)' },
        { name: 'AI Voice Booking', icon: Mic, included: false },
        { name: 'SMS Notifications', icon: Smartphone, included: false },
        { name: 'Self-Service Kiosk', icon: Monitor, included: false },
        { name: 'Multi-Location', icon: MapPin, included: false },
      ],
      cta: 'Start Free Trial'
    },
    {
      name: 'Growth',
      description: 'Most popular for growing restaurants',
      price: annual ? 79 : 99,
      period: '/month',
      highlight: true,
      badge: 'Most Popular',
      features: [
        { name: 'Everything in Starter', icon: Check, included: true, highlight: true },
        { name: 'AI Voice Booking', icon: Mic, included: true, description: '24/7 automated phone reservations' },
        { name: 'SMS Reminders & Notifications', icon: Smartphone, included: true, description: 'Automated SMS to customers' },
        { name: 'Loyalty Program', icon: Gift, included: true, description: 'Points & rewards system' },
        { name: 'Gift Cards', icon: CreditCard, included: true, description: 'Digital & physical gift cards' },
        { name: 'Payment Integration', icon: Shield, included: true, description: 'Integrated payment processing' },
        { name: 'Advanced Scheduling', icon: Calendar, included: true, description: 'Employee management & payroll' },
        { name: 'Priority Support', icon: MessageSquare, included: true, description: 'Priority email & chat support' },
        { name: 'Self-Service Kiosk', icon: Monitor, included: false },
        { name: 'Multi-Location', icon: MapPin, included: false },
      ],
      cta: 'Start Free Trial'
    },
    {
      name: 'Pro',
      description: 'For multi-location and high-volume restaurants',
      price: annual ? 159 : 199,
      period: '/month',
      highlight: false,
      features: [
        { name: 'Everything in Growth', icon: Check, included: true, highlight: true },
        { name: 'Self-Service Kiosk Ordering', icon: Monitor, included: true, description: 'Unattended kiosk system with payments' },
        { name: 'Advanced AI Automation', icon: Brain, included: true, description: 'AI-powered operations & insights' },
        { name: 'Multi-Location Support', icon: MapPin, included: true, description: 'Manage multiple restaurants' },
        { name: 'Advanced Analytics', icon: TrendingUp, included: true, description: 'Deep insights & custom reports' },
        { name: 'Staff Performance Tracking', icon: Users, included: true, description: 'Employee metrics & KPIs' },
        { name: 'White-Label Options', icon: Sparkles, included: true, description: 'Custom branding available' },
        { name: '24/7 Phone Support', icon: MessageSquare, included: true, description: 'Dedicated support team' },
        { name: 'API Access', icon: Zap, included: true, description: 'Custom integrations' },
      ],
      cta: 'Start Free Trial'
    }
  ];

  const addons = [
    { 
      name: 'AI Usage Credits', 
      icon: Brain,
      price: 'From $0.01/call',
      description: 'Additional AI voice & automation credits beyond included limits',
      highlight: true
    },
    { 
      name: 'SMS Message Bundles', 
      icon: Smartphone,
      price: '$19/mo',
      description: '1,000 SMS credits/month (beyond included limits)'
    },
    { 
      name: 'Additional Kiosk Device', 
      icon: Monitor,
      price: '$49/mo',
      description: 'Extra self-service kiosk license per location'
    },
    { 
      name: 'Additional Location', 
      icon: MapPin,
      price: '$99/mo',
      description: 'Add another restaurant location to your account'
    },
    { 
      name: 'Advanced Analytics Suite', 
      icon: TrendingUp,
      price: '$39/mo',
      description: 'Enhanced reporting, forecasting & predictive insights'
    },
    { 
      name: 'Dedicated Account Manager', 
      icon: Users,
      price: '$299/mo',
      description: 'Personal onboarding & ongoing support specialist'
    },
  ];

  const faqs = [
    {
      question: 'Is there a free trial?',
      answer: 'Yes! All plans come with a 7-day free trial. No credit card required to start. You can explore all features before committing.'
    },
    {
      question: 'What\'s included in the AI Voice Booking?',
      answer: 'AI Voice Booking uses advanced speech recognition to handle phone reservations 24/7. It can check availability, book tables, send confirmations, and handle modifications. Includes 100 AI calls/month on Growth plan, 500/month on Pro plan.'
    },
    {
      question: 'How does the Self-Service Kiosk work?',
      answer: 'Our kiosk system is a fully unattended ordering solution with integrated payment processing. Customers can browse your menu, customize orders, and pay directly. Perfect for fast-casual and QSR environments. Hardware optional - works on any tablet.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and ACH bank transfers for annual plans.'
    },
    {
      question: 'Can I change plans later?',
      answer: 'Absolutely. You can upgrade or downgrade your plan at any time. Changes take effect immediately, with prorated billing adjustments.'
    },
    {
      question: 'What about payment processing fees?',
      answer: 'Payment processing is 2.6% + 10¢ per transaction for cards. We offer competitive rates and transparent pricing with no hidden fees. Crypto payments also available.'
    },
    {
      question: 'Is multi-location support truly unlimited?',
      answer: 'On the Pro plan, you get up to 3 locations included. Each additional location is $99/month. All locations share unified reporting, menu management, and customer data.'
    },
    {
      question: 'Do you have long-term contracts?',
      answer: 'No contracts! All plans are month-to-month. Annual plans offer a 20% discount but you can cancel anytime with no penalty.'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
              <span className="text-xl font-bold text-white">Chain29</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/product" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
                Product
              </Link>
              <Link to="/pricing" className="text-white text-sm font-medium">
                Pricing
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
                Support
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10">
                    Login
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/owner/login" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Restaurant Owner</div>
                        <div className="text-xs text-muted-foreground">Manage your restaurant</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/login" className="flex items-center gap-2 cursor-pointer">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Employee / Staff</div>
                        <div className="text-xs text-muted-foreground">Clock in & access POS</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Customer</div>
                        <div className="text-xs text-muted-foreground">Order food online</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/owner/signup">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4 text-center">
          <Badge className="mb-4 bg-orange-500/20 text-orange-400 hover:bg-orange-500/20 border-orange-500/30">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered Restaurant Technology
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            No hidden fees. No long-term contracts. Choose the plan that fits your restaurant's needs.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-medium ${!annual ? 'text-white' : 'text-slate-400'}`}>Monthly</span>
            <Switch 
              checked={annual} 
              onCheckedChange={setAnnual}
              className="data-[state=checked]:bg-orange-500"
            />
            <span className={`text-sm font-medium ${annual ? 'text-white' : 'text-slate-400'}`}>
              Annual
              <Badge className="ml-2 bg-green-500/20 text-green-400 hover:bg-green-500/20 border-green-500/30">
                Save 20%
              </Badge>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
            {plans.map((plan) => (
              <Card 
                key={plan.name}
                className={`relative bg-white/5 border-white/10 hover:bg-white/[0.07] transition-all duration-300 ${
                  plan.highlight ? 'border-orange-500 ring-2 ring-orange-500/20 scale-105 lg:scale-110' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 px-4 py-1.5">
                      <Star className="w-3 h-3 mr-1" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4 pt-8">
                  <CardTitle className="text-2xl lg:text-3xl text-white mb-2">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-400 text-base">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="text-center mb-6 pb-6 border-b border-white/10">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl text-slate-400">$</span>
                      <span className="text-5xl lg:text-6xl font-bold text-white">{plan.price}</span>
                      <span className="text-slate-400">{plan.period}</span>
                    </div>
                    {annual && (
                      <p className="text-sm text-green-400 mt-2">Billed annually (${plan.price * 12}/year)</p>
                    )}
                  </div>
                  
                  <Link to="/owner/signup">
                    <Button 
                      className={`w-full mb-6 ${
                        plan.highlight 
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30' 
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="group">
                        <div className="flex items-start gap-3">
                          {feature.included ? (
                            feature.highlight ? (
                              <div className="w-5 h-5 flex-shrink-0 mt-0.5 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            ) : (
                              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                            )
                          ) : (
                            <X className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {feature.icon && <feature.icon className={`w-4 h-4 ${feature.included ? 'text-orange-400' : 'text-slate-600'}`} />}
                              <span className={`${feature.included ? 'text-slate-200 font-medium' : 'text-slate-500'}`}>
                                {feature.name}
                              </span>
                            </div>
                            {feature.description && feature.included && (
                              <p className="text-xs text-slate-500 mt-0.5 ml-6">{feature.description}</p>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-16 border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 hover:bg-purple-500/20 border-purple-500/30">
              <Zap className="w-3 h-3 mr-1" />
              Flexible Add-ons
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Scale Your Way</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Enhance any plan with additional features and capacity as your business grows
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {addons.map((addon, idx) => (
              <Card key={idx} className="bg-white/5 border-white/10 hover:bg-white/[0.07] hover:border-orange-500/30 transition-all duration-300 group">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center flex-shrink-0 group-hover:from-orange-500/30 group-hover:to-orange-600/30 transition-colors">
                      <addon.icon className="w-6 h-6 text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-white font-semibold">{addon.name}</h3>
                        {addon.highlight && (
                          <Badge className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/20 border-orange-500/30 text-xs">
                            Popular
                          </Badge>
                        )}
                      </div>
                      <p className="text-orange-400 font-bold text-lg mb-2">{addon.price}</p>
                      <p className="text-sm text-slate-400">{addon.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-slate-400">
              Need a custom solution? <Link to="/contact" className="text-orange-400 hover:text-orange-300 underline">Contact our sales team</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Compare Plans</h2>
            <p className="text-lg text-slate-300">See what's included in each plan</p>
          </div>
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
              <BarChart3 className="w-12 h-12 text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl text-white font-semibold mb-2">Need Detailed Comparison?</h3>
              <p className="text-slate-400 mb-4">View our comprehensive feature comparison chart with all capabilities</p>
              <Link to="/product">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/20">
                  View Full Feature List
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-300">Everything you need to know about our pricing</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`}
                className="bg-white/5 border border-white/10 rounded-lg px-6 hover:bg-white/[0.07] transition-colors"
              >
                <AccordionTrigger className="text-white hover:no-underline text-left">
                  <span className="font-medium">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-300 text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 border-t border-white/10 bg-white/[0.02]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <Shield className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Secure & Compliant</h3>
              <p className="text-sm text-slate-400">PCI-DSS compliant payment processing</p>
            </div>
            <div>
              <Zap className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">99.9% Uptime</h3>
              <p className="text-sm text-slate-400">Cloud-based reliability you can trust</p>
            </div>
            <div>
              <Users className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">24/7 Support</h3>
              <p className="text-sm text-slate-400">We're here when you need us</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Ready to Transform Your Restaurant?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of restaurants using Chain29 to streamline operations and grow revenue.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/owner/signup">
              <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg shadow-xl shadow-orange-500/30">
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/20 px-8 py-6 text-lg">
                Schedule Demo
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-400 mt-6">No credit card required • Cancel anytime • Setup in minutes</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-6 w-6" />
              <span className="text-white font-semibold">Chain29</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
              <Link to="/product" className="hover:text-white transition-colors">Product</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-slate-500">© 2026 Chain29. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
